import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", company_code: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await login(form.email.trim(), form.password, form.company_code.trim());
      toast.success("Welcome back");
      // Route to "/" so RoleHome picks the correct landing per role
      navigate("/");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7EF] bg-grain flex items-stretch">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] p-12 bg-[url('https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=1600&q=80')] bg-cover bg-center relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#083F22]/85 via-[#083F22]/50 to-transparent" />
        <div className="relative z-10">
          <BrandLogo size={48} showWordmark tagline variant="dark"/>
          <div className="mt-10 font-serif text-5xl leading-tight max-w-md text-white">
            We shape gold, you shape stories.
          </div>
        </div>
        <div className="relative z-10 text-white/85 text-sm max-w-md">
          Manufacturing orders, karigar workflows, and a CRM built for occasion-driven sales — beautifully unified.
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md border-[#E4DFCF] shadow-[0_8px_24px_rgba(20,38,28,0.06)] rounded-2xl">
          <CardHeader className="pb-4">
            <BrandLogo size={40} showWordmark tagline testId="login-brand"/>
            <div className="font-serif text-3xl font-semibold mt-6" data-testid="login-title">Sign in</div>
            <div className="text-sm text-[#4A5A50] mt-1">Enter your company code and credentials to continue.</div>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="company_code" className="text-xs uppercase tracking-[0.2em] text-[#4A5A50]">Company Code</Label>
                <Input
                  id="company_code"
                  data-testid="login-company-code"
                  value={form.company_code}
                  onChange={(e) => setForm({ ...form, company_code: e.target.value.toUpperCase() })}
                  className="rounded-lg"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-[#4A5A50]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-lg"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-[#4A5A50]">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="rounded-lg"
                  required
                />
              </div>

              {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}

              <Button type="submit" className="w-full rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-11" data-testid="login-submit" disabled={loading}>
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Sign in
              </Button>

              <div className="text-center text-sm text-[#4A5A50]">
                New to TARU?{" "}
                <Link to="/signup" className="text-[#0F5E39] hover:underline" data-testid="signup-link">Create workspace</Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
